import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { NumberField } from "./NumberField";
import {
  type NumberFieldDataProps,
  numberFieldDataSchema,
} from "./NumberField.schema";
import { NUMBER_FIELD_TAG_NAME } from "./constants";

export interface NumberFieldHandle {
  disabled: (value?: boolean) => void;
  setError: (message?: string | null) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [NUMBER_FIELD_TAG_NAME]: {
      props: NumberFieldDataProps;
      handle: NumberFieldHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-number-field": HTMLElement & NumberFieldHandle;
  }
}

type NumberFieldSlots = {
  hasLeftSection: boolean;
  hasRightSection: boolean;
};

function readNumberFieldSlotted(el: HTMLElement): NumberFieldSlots {
  return {
    hasLeftSection: Boolean(el.querySelector(':scope > [slot="left"]')),
    hasRightSection: Boolean(el.querySelector(':scope > [slot="right"]')),
  };
}

export function RWCNumberField({
  ref,
  disabled: initialDisabled,
  value: initialValue,
  hasLeftSection,
  hasRightSection,
  ...props
}: NumberFieldDataProps &
  Partial<NumberFieldSlots> & { ref?: Ref<NumberFieldHandle> }) {
  const host = useIslandHost();

  const [disabled, setDisabled] = useState(initialDisabled ?? false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const currentValue = useRef<number | null>(initialValue ?? null);
  const committedValue = useRef<number | null>(initialValue ?? null);

  const handle: NumberFieldHandle = {
    disabled: (value = true) => setDisabled(value),
    setError: (message = null) => setErrorMessage(message),
  };

  useImperativeHandle(ref, () => handle, []);

  const syncFormValue = useCallback(() => {
    getElementInternals(host)?.setFormValue(
      disabled || currentValue.current == null
        ? null
        : String(currentValue.current),
    );
  }, [host, disabled]);

  useEffect(syncFormValue, [syncFormValue]);

  function handleValueChange(value: number | null) {
    currentValue.current = value;
    syncFormValue();
  }

  function handleValueCommitted(value: number | null) {
    if (value === committedValue.current) {
      return;
    }

    committedValue.current = value;
    host?.setAttribute("value", value == null ? "" : String(value));
    host?.dispatchEvent(
      new CustomEvent("number-field:change", {
        detail: { value },
        bubbles: true,
      }),
    );
  }

  return (
    <NumberField
      {...props}
      leftSection={hasLeftSection ? <slot name="left" /> : undefined}
      rightSection={hasRightSection ? <slot name="right" /> : undefined}
      defaultValue={initialValue}
      disabled={disabled}
      invalid={errorMessage != null}
      onValueChange={handleValueChange}
      onValueCommitted={handleValueCommitted}
    />
  );
}

componentManager.register(
  rwc(NUMBER_FIELD_TAG_NAME, {
    component: RWCNumberField,
    dataSchema: numberFieldDataSchema,
    usesSlots: true,
    readSlotted: readNumberFieldSlotted,
    formAssociated: true,
  }),
);
